import React, { useRef, useEffect, useState, useCallback } from 'react'
import SpinnerIcon from '../common/SpinnerIcon'
import TrashIcon from '../common/TrashIcon'

const DELETE_BUTTON_WIDTH = 100
const TRANSITION_CLASS =
  'relative z-[2] transition-transform duration-300 ease-out'
const NO_TRANSITION_CLASS = 'relative z-[2]'

function SwipeableListItem({
  className,
  children,
  onDelete
}: {
  className?: string
  children: React.ReactNode
  onDelete?: () => void
}) {
  const listElementRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const rafIdRef = useRef<number>(0)
  const deleteTimeoutRef = useRef<number>(0)

  const dragStartXRef = useRef(0)
  const dragStartYRef = useRef(0)
  const leftRef = useRef(0)
  const draggedRef = useRef(false)
  const swipeActivatedRef = useRef(false)
  const [isOpen, setIsOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const updatePosition = useCallback(() => {
    if (!draggedRef.current || !swipeActivatedRef.current) return

    const element = listElementRef.current
    if (element) {
      element.style.transform = `translateX(${leftRef.current}px)`
    }

    rafIdRef.current = requestAnimationFrame(updatePosition)
  }, [])

  const onMouseMove = useCallback((evt: MouseEvent) => {
    if (!swipeActivatedRef.current) return
    const left = evt.clientX - dragStartXRef.current
    leftRef.current = left < 0 ? Math.max(left, -DELETE_BUTTON_WIDTH) : 0
  }, [])

  const onTouchMove = useCallback(
    (evt: TouchEvent) => {
      const touch = evt.targetTouches[0]
      if (!touch) return

      const deltaX = touch.clientX - dragStartXRef.current
      const deltaY = touch.clientY - dragStartYRef.current

      // If swipe is not yet activated, check if this is a horizontal swipe
      if (!swipeActivatedRef.current) {
        const absDeltaX = Math.abs(deltaX)
        const absDeltaY = Math.abs(deltaY)

        // Need minimum movement to activate
        if (absDeltaX < 10 && absDeltaY < 10) {
          return
        }

        // Only activate swipe if horizontal movement is significantly greater than vertical
        if (absDeltaX > absDeltaY * 1.5 && absDeltaX > 10) {
          swipeActivatedRef.current = true
          draggedRef.current = true
          const element = listElementRef.current
          if (element) {
            element.className = NO_TRANSITION_CLASS
          }
          rafIdRef.current = requestAnimationFrame(updatePosition)
          // Prevent scrolling once swipe is activated
          evt.preventDefault()
        } else if (absDeltaY > absDeltaX) {
          // This is a vertical scroll, cancel the gesture
          return
        }
      }

      if (swipeActivatedRef.current) {
        evt.preventDefault()
        const left = deltaX
        leftRef.current = left < 0 ? Math.max(left, -DELETE_BUTTON_WIDTH) : 0
      }
    },
    [updatePosition]
  )

  const onDragEnd = useCallback(() => {
    if (!draggedRef.current) {
      swipeActivatedRef.current = false
      return
    }

    draggedRef.current = false
    swipeActivatedRef.current = false
    const threshold = DELETE_BUTTON_WIDTH / 2

    if (Math.abs(leftRef.current) > threshold) {
      leftRef.current = -DELETE_BUTTON_WIDTH
      setIsOpen(true)
    } else {
      leftRef.current = 0
      setIsOpen(false)
    }

    const element = listElementRef.current
    if (element) {
      element.className = TRANSITION_CLASS
      element.style.transform = `translateX(${leftRef.current}px)`
    }

    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = 0
    }
  }, [])

  const onDragEndMouse = useCallback(() => {
    window.removeEventListener('mousemove', onMouseMove)
    onDragEnd()
  }, [onMouseMove, onDragEnd])

  const onDragEndTouch = useCallback(() => {
    window.removeEventListener('touchmove', onTouchMove)
    onDragEnd()
  }, [onTouchMove, onDragEnd])

  const onDragStart = useCallback(
    (clientX: number) => {
      // For mouse, activate immediately
      draggedRef.current = true
      swipeActivatedRef.current = true
      dragStartXRef.current = clientX + Math.abs(leftRef.current)

      const element = listElementRef.current
      if (element) {
        element.className = NO_TRANSITION_CLASS
      }

      rafIdRef.current = requestAnimationFrame(updatePosition)
    },
    [updatePosition]
  )

  const onDragStartMouse = useCallback(
    (evt: React.MouseEvent) => {
      onDragStart(evt.clientX)
      window.addEventListener('mousemove', onMouseMove, { passive: true })
    },
    [onDragStart, onMouseMove]
  )

  const onDragStartTouch = useCallback(
    (evt: React.TouchEvent) => {
      const touch = evt.targetTouches[0]
      if (!touch) return
      // For touch, store initial position but don't activate yet
      dragStartXRef.current = touch.clientX + Math.abs(leftRef.current)
      dragStartYRef.current = touch.clientY
      swipeActivatedRef.current = false
      window.addEventListener('touchmove', onTouchMove, { passive: false })
    },
    [onTouchMove]
  )

  const handleDelete = useCallback(() => {
    setIsDeleting(true)
    const wrapper = wrapperRef.current
    if (wrapper) {
      wrapper.style.maxHeight = '0'
    }
    deleteTimeoutRef.current = window.setTimeout(() => {
      onDelete?.()
    }, 500)
  }, [onDelete])

  const handleClose = useCallback(() => {
    leftRef.current = 0
    setIsOpen(false)
    const element = listElementRef.current
    if (element) {
      element.className = TRANSITION_CLASS
      element.style.transform = 'translateX(0)'
    }
  }, [])

  useEffect(() => {
    window.addEventListener('mouseup', onDragEndMouse)
    window.addEventListener('touchend', onDragEndTouch)

    return () => {
      window.removeEventListener('mouseup', onDragEndMouse)
      window.removeEventListener('touchend', onDragEndTouch)
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('touchmove', onTouchMove)

      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
      }
      if (deleteTimeoutRef.current) {
        clearTimeout(deleteTimeoutRef.current)
      }
    }
  }, [onDragEndMouse, onDragEndTouch, onMouseMove, onTouchMove])

  return (
    <div
      className={`relative transition-[max-height] duration-500 ease-in-out max-h-[1000px] origin-top overflow-hidden w-full cursor-pointer text-lg ${className || ''}`}
      ref={wrapperRef}>
      <div className="absolute w-full h-full z-[1] flex flex-row justify-end items-center">
        <button
          className="w-[100px] h-full bg-transparent text-white border-none text-sm font-semibold cursor-pointer flex items-center justify-center hover:bg-black/10 active:bg-black/20"
          onClick={handleDelete}
          type="button"
          disabled={isDeleting}>
          <div className="rounded-full p-2 transition-colors bg-[#ee2650]">
            {isDeleting ? (
              <SpinnerIcon className="text-white animate-spin" />
            ) : (
              <TrashIcon className="text-white" />
            )}
          </div>
        </button>
      </div>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        className={TRANSITION_CLASS}
        ref={listElementRef}
        onMouseDown={onDragStartMouse}
        onTouchStart={onDragStartTouch}
        onClick={isOpen ? handleClose : undefined}>
        {children}
      </div>
    </div>
  )
}

export default SwipeableListItem
